import { useEffect, useState } from "react";

/**
 * useMobileOrientation — reactive hook telling you whether the user is on a
 * phone (mobile, NOT tablet) AND whether they're in portrait orientation.
 *
 * Detection rules:
 *  - isMobile: coarse pointer (touch) AND viewport width < 768px (tablets ≥768).
 *  - isPortrait: matchMedia("(orientation: portrait)").
 *
 * Both values update automatically when the user rotates or resizes.
 */
export const useMobileOrientation = () => {
  const compute = () => {
    if (typeof window === "undefined") return { isMobile: false, isPortrait: false };
    const coarsePointer = window.matchMedia("(pointer: coarse)").matches;
    const narrow = window.innerWidth < 768;
    const isMobile = coarsePointer && narrow;
    const isPortrait = window.matchMedia("(orientation: portrait)").matches;
    return { isMobile, isPortrait };
  };

  const [state, setState] = useState(compute);

  useEffect(() => {
    const update = () => setState(compute());
    const orientationMq = window.matchMedia("(orientation: portrait)");
    const pointerMq = window.matchMedia("(pointer: coarse)");

    // Modern browsers use addEventListener; older Safari uses addListener.
    const bind = (mq) => {
      if (mq.addEventListener) mq.addEventListener("change", update);
      else if (mq.addListener) mq.addListener(update);
    };
    const unbind = (mq) => {
      if (mq.removeEventListener) mq.removeEventListener("change", update);
      else if (mq.removeListener) mq.removeListener(update);
    };

    bind(orientationMq);
    bind(pointerMq);
    window.addEventListener("resize", update);
    window.addEventListener("orientationchange", update);

    return () => {
      unbind(orientationMq);
      unbind(pointerMq);
      window.removeEventListener("resize", update);
      window.removeEventListener("orientationchange", update);
    };
  }, []);

  return state;
};

export default useMobileOrientation;
